import { inject, Injectable, Type } from '@angular/core';
import { HisConfpackageIconComponent } from '../component/metadata/confpackage/his-confpackage-icon.component';
import { HisSignedPdfResourceIconComponent } from '../component/metadata/signedPdf/his-signed-pdf-resource-icon.component';
import { HIS_RESOURCE_PROPERTY_DISPLAYS, HisResourcePropertyDisplay } from '../model/his-resource-property-display.model';
import { HisDataSourceIconComponent } from '../component/metadata/dataSource/his-data-source-icon.component';

/**
 * The displays shipped with cs.sys.hisng, mirroring the categories the JSF metadata templates know.
 */
const BUILT_IN_DISPLAYS: HisResourcePropertyDisplay[] = [
    {
        categoryKey: 'signedPdfResource',
        icon: HisSignedPdfResourceIconComponent,
        showInOverlay: false,
    },
    {
        categoryKey: 'confpackage',
        icon: HisConfpackageIconComponent,
    },
    {
        categoryKey: 'dataSource',
        icon: HisDataSourceIconComponent,
        showInOverlay: false,
    }
];

/**
 * Knows how the properties of a resource category are presented: which component renders the
 * icon next to the filename and which one the details inside the properties overlay.
 *
 * Applications extend it through {@link HIS_RESOURCE_PROPERTY_DISPLAYS}; a display registered
 * there replaces the built-in one for the same category.
 */
@Injectable( { providedIn: 'root' } )
export class HisResourcePropertyDisplayService {

    private readonly displays = new Map<string, HisResourcePropertyDisplay>(
        [...BUILT_IN_DISPLAYS, ...( inject( HIS_RESOURCE_PROPERTY_DISPLAYS, { optional: true } ) ?? [] )]
            .map( ( display ) => [display.categoryKey, display] )
    );

    /** The component showing the given category next to the filename, if there is one. */
    public iconOf( categoryKey: string ): Type<unknown> | null {
        return this.displays.get( categoryKey )?.icon ?? null;
    }

    /** The component showing the given category in the properties overlay, if there is one. */
    public detailsOf( categoryKey: string ): Type<unknown> | null {
        return this.displays.get( categoryKey )?.details ?? null;
    }

    /** Whether the category should be included in the general properties overlay. */
    public showInOverlay( categoryKey: string ): boolean {
        return this.displays.get( categoryKey )?.showInOverlay !== false;
    }
}
